import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../../src/components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { TResponseModel, TRole, TUser } from "../../../types/config";
import { TFilterModel } from "../../../types/config";
import { ePrivileges, eResultCode, eRoleUniqueId } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  dateFormat,
  dateOnlyFormat,
  defaultFilterParams,
  PageSize,
  STAKE_HOLDERS,
} from "../../../utils/constants";
import {
  Button,
  Drawer,
  Space,
  Input,
  Table,
  TablePaginationConfig,
  Tooltip,
  Dropdown,
} from "antd";
import RoleForm from "./form";
import CustomDrawer from "../../../components/drawer";
import { GetRoleList } from "../../../utils/api.constant";
import { UserContext } from "../../../context";
import { useNavigate } from "react-router-dom";
import utils from "../../../utils";
import ActionCard from "../../../components/action-card/action-card";
import {
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import DateUtils from "../../../utils/date";
import ExportToCSV from "../../../components/export-to-csv/export-to-csv";

/*
 * RoleList Component
 * This component is responsible for displaying and managing the list of roles.
 * It handles fetching role data, searching, filtering, and rendering the role list.
 */
export default function RoleList() {
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TUser[]>([]);
  const { onShowToast } = useToast();
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  // const [stakeHolderName, setStakeHolderName] = useState<string>(
  //   activeUser?.stakeHolderName || STAKE_HOLDERS.IDEX
  // );

  useEffect(() => {
    setActiveUser(context?.user);
  }, [context?.user]);

  const handleSaveColumns = () => {
    setDrawerVisible(false);
    setColumns(draftColumns);
  };

  const initialColumns: any[] = [
    {
      title: "Sr. No.",
      key: "serialNumber",
      // width: "8%",
      render: (_: any, __: TRole, index: number) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name", // Ensure this key exists in TUser
      key: "name",
      // width: "25%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TRole, b: TRole) => a.name?.localeCompare(b.name ?? "") || 0,
      onFilter: (value: string | number | boolean, record: TRole) =>
        record.name?.includes(value as string),
    },
    // {
    //   title: "Role Unique Id",
    //   dataIndex: "roleUniqueId",
    //   key: "roleUniqueId",
    //   width: "25%",
    //   filterSearch: true,
    //   filters: utils.getUniqueFilters(data, "roleUniqueId"),
    //   sorter: (a: TRole, b: TRole) =>
    //     a.roleUniqueId?.localeCompare(b.roleUniqueId ?? "") || 0,
    //   onFilter: (value: string | number | boolean, record: TRole) =>
    //     record.roleUniqueId?.includes(value as string),
    // },

    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      // width: "30%",
      filterSearch: true,
      sorter: (a: TRole, b: TRole) =>
        a.description?.localeCompare(b.description ?? "") || 0,
      filters: utils.getUniqueFilters(data, "description"),
      onFilter: (value: string | number | boolean, record: TRole) =>
        record.description?.includes(value as string),
    },
    {
      title: "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      filterSearch: true,
      sorter: (a: TRole, b: TRole) =>
        String(a.createdOn)?.localeCompare(String(b.createdOn ?? "")) || 0,
      render: (createdOn: string) => {
        return DateUtils.format(createdOn, dateOnlyFormat);
      },
    },
    {
      title: "Action",
      key: "actions",
      align: "left",
      // width: "10%",
      render: (role: TRole) => (
        <>
          {Utils.isUserHasAccess(
            context?.user?.privilegeList,
            ePrivileges.EDITROLE,
          ) &&
          role?.roleUniqueId?.toLocaleUpperCase() !==
            eRoleUniqueId.SYSTEM_ADMIN &&
          role?.roleUniqueId?.toLocaleUpperCase() !==
            eRoleUniqueId.CUSTOM_ADMIN ? (
            <Dropdown
              trigger={["click"]}
              getPopupContainer={() => document.body}
              menu={{
                items: [
                  {
                    key: "edit",
                    label: (
                      <CustomDrawer
                        placement="right"
                        width={800}
                        trigger={
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              alignItems: "center",
                            }}
                          >
                            <EditOutlined style={{ fontSize: "12px" }} />
                            Edit
                          </div>
                        }
                        component={RoleForm}
                        componentProps={{
                          id: role.id,
                          onRefreshList: () => fetchData(),
                        }}
                        title="Edit Role"
                      />
                    ),
                  },
                ],
              }}
            >
              <MoreOutlined style={{ fontSize: 18, cursor: "pointer" }} />
            </Dropdown>
          ) : (
            <MoreOutlined
              style={{
                fontSize: 18,
                cursor: "pointer",
                color: "var(--bs-action-disabled)",
              }}
              onClick={() => {
                onShowToast({
                  type: ToastType.info,
                  title: <InfoCircleOutlined />,
                  position: ToastOpen.centerTop,
                  content: "Access not available",
                });
              }}
            />
          )}
        </>
      ),
    },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setColumns(initialColumns);
  }, [context?.user?.privilegeList]);

  const [columns, setColumns] = useState(initialColumns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [draftColumns, setDraftColumns] = useState(columns); // Staging state for changes
  // To fetch the list of all the users
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: PageSize,
        },
      };
      const response: TResponseModel = await post(GetRoleList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TRole, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          name: item.name,
          roleUniqueId: item.roleUniqueId,
          createdOn: item.createdOn,
          description: item.description,
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(data.map((item: TRole) => item.name)),
        ).map((name) => ({ text: name, value: name }));
        const roleUniqueIdFilters = Array.from(
          new Set(data.map((item: TRole) => item.roleUniqueId)),
        ).map((stakeHolderName) => ({
          text: stakeHolderName,
          value: stakeHolderName,
        }));
        const descriptionFilters = Array.from(
          new Set(data.map((item: TRole) => item.description)),
        ).map((description) => ({ text: description, value: description }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: nameFilters };
            }
            if (col.key === "roleUniqueId") {
              return { ...col, filters: roleUniqueIdFilters };
            }
            if (col.key === "description") {
              return { ...col, filters: descriptionFilters };
            }
            return col;
          }),
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action",
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
    isActive: user.isActive ? "Active" : "Inactive",
    createdOn: DateUtils.format(user.createdOn, dateOnlyFormat),
  }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
  };
  // setTableKey((prev) => prev + 1);
  // fetchData(1, 10); //

  return (
    <div className="pageBody">
      <PageHeader
        icon={<TeamOutlined />}
        title={`${filterParams.totalRows} Total Roles`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDROLE,
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={RoleForm}
              componentProps={{
                id: 0,
                onRefreshList: () => {
                  navigate("/dashboard/roles");
                  fetchData();
                },
              }}
              title="Add New Role"
              width={800}
            />
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            prefix={
              <SearchOutlined style={{ color: "var( --bs-search-style)" }} />
            }
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`roles_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          // sticky
          scroll={{
            y: "calc(100vh - 310px)",
            x: "max-content",
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
